import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { OfferForm, type OfferRow } from '../components/CatalogueForms';
import { AllowanceEditor } from '../components/AllowanceEditor';

type Price = { currency: string; base_price: number; unit_price: number };
type Offer = OfferRow & {
  service_lines: { name_i18n: Record<string, string>; code: string } | null;
  catalogue_offer_prices: Price[];
};

export function Catalogue() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<OfferRow | null>(null);
  const [allowEdit, setAllowEdit] = useState<{ id: string; name: string } | null>(null);

  const load = useCallback(async () => {
    const r = await supabase.from('catalogue_offers')
      .select('id,name,kind,property_type,service_line_id,billing_period,included_visits,pricing_model,active,description,service_lines(name_i18n,code),catalogue_offer_prices(currency,base_price,unit_price)')
      .is('deleted_at', null).order('name');
    setRows((r.data as unknown as Offer[]) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const slName = (o: Offer) => o.service_lines ? (o.service_lines.name_i18n?.[i18n.language] ?? o.service_lines.name_i18n?.en ?? o.service_lines.code) : null;
  const scope = (o: Offer) => [o.property_type ? t('propertyType.' + o.property_type) : t('cat.anyType'), slName(o)].filter(Boolean).join(' · ');
  const priceStr = (o: Offer) => (o.catalogue_offer_prices ?? []).map((p) =>
    formatCurrency(p.base_price, p.currency, i18n.language) + (o.pricing_model !== 'flat' && p.unit_price ? ` + ${formatCurrency(p.unit_price, p.currency, i18n.language)}/${t(o.pricing_model === 'per_area' ? 'cat.unitArea' : 'cat.unitEquip')}` : '')
  ).join('  ·  ') || '—';

  return (
    <Layout header={<Topbar actions={<Button onClick={() => { setEditing(null); setFormOpen(true); }}>+ {t('cat.newOffer')}</Button>} />}>
      <div className="px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.catalogue')}</h1>
      </div>
      <div className="px-7 pb-7 pt-[18px]">
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead><tr className="text-left text-xs text-muted">
                <th className="px-5 py-3 font-medium">{t('form.name')}</th>
                <th className="px-2 py-3 font-medium">{t('cat.kind')}</th>
                <th className="px-2 py-3 font-medium">{t('cat.scope')}</th>
                <th className="px-2 py-3 font-medium">{t('cat.includedVisits')}</th>
                <th className="px-2 py-3 font-medium">{t('cat.prices')}</th>
                <th className="px-2 py-3 font-medium">{t('crm.active')}</th>
                <th className="px-5 py-3" />
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                  : rows.length === 0 ? <tr><td colSpan={7} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>
                  : rows.map((o) => (
                    <tr key={o.id} className="border-t border-divider hover:bg-bg">
                      <td className="px-5 py-3.5 font-medium">{o.name}</td>
                      <td className="px-2 py-3.5 text-muted">{t('cat.kind_' + o.kind)}{o.kind === 'subscription' && o.billing_period ? ` · ${t('cat.period_' + o.billing_period)}` : ''}</td>
                      <td className="px-2 py-3.5 text-muted">{scope(o)}</td>
                      <td className="px-2 py-3.5 font-mono text-muted">{o.included_visits ?? '—'}</td>
                      <td className="px-2 py-3.5 font-mono text-xs">{priceStr(o)}</td>
                      <td className="px-2 py-3.5">{o.active ? <StatusPill tone="done">{t('common.yes')}</StatusPill> : <StatusPill tone="open">{t('common.no')}</StatusPill>}</td>
                      <td className="px-5 py-3.5 text-right">
                        {o.kind === 'subscription' && <Button variant="ghost" size="sm" onClick={() => setAllowEdit({ id: o.id, name: o.name })}>{t('allow.params')}</Button>}
                        <Button variant="ghost" size="sm" onClick={() => { setEditing(o); setFormOpen(true); }}>{t('form.edit')}</Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <OfferForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
      {allowEdit && <AllowanceEditor kind="offer" id={allowEdit.id} name={allowEdit.name} onClose={() => setAllowEdit(null)} onSaved={load} />}
    </Layout>
  );
}
